// src/App.js - Enhanced version with platform theme management
import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import AuthHeroPage from "./components/AuthHeroPage";  // Changed from HeroPage
import PlatformSelector from "./components/PlatformSelector";  // Added import
import Dashboard from "./components/Dashboard";
import OAuthSuccess from "./components/OAuthSuccess";  // Added OAuth success handler
import { SyncProvider } from "./hooks/SyncCoordinator";
import { SupabaseAuthProvider } from "./hooks/useSupabaseAuth";
import SupabaseAuthStyleTest from "./components/SupabaseAuthStyleTest";  // Renamed from AuthTest for CSS testing
import OAuthCallback from "./components/Auth/OAuthCallback";
import { supabase } from "./lib/supabase";  // Import Supabase client
import { API_BASE_URL } from './config';
import logger from './services/logger';

// Utility functions for Error Boundary
const detectAIError = (error) => {
  return error.message?.includes('AI') ||
         error.message?.includes('OpenAI') ||
         error.message?.includes('ElevenLabs') ||
         error.stack?.includes('ai_service') ||
         error.stack?.includes('generateAI');
};

const clearAICache = () => {
  try {
    const cacheKeys = Object.keys(localStorage).filter(key =>
      key.includes('ai_') || key.includes('cache_') || key.includes('usage_')
    );
    cacheKeys.forEach(key => localStorage.removeItem(key));
    logger.debug('Cleared AI cache after error');
  } catch (e) {
    logger.warn('Could not clear AI cache:', e);
  }
};

const logAIError = (error, errorInfo) => {
  logger.error('AI Service Error Details');
  logger.error('Error:', error);
  logger.error('Stack:', error.stack);
  logger.error('Component Stack:', errorInfo.componentStack);
};

// Error display components
const ErrorDisplay = ({ error, isAIError, retryCount, onRetry }) => (
  <div style={{
    padding: '20px',
    textAlign: 'center',
    backgroundColor: '#1a1a1a',
    color: 'white',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  }}>
    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
      <h2>{isAIError ? '🤖 AI Service Error' : '⚠️ Something went wrong'}</h2>

      <ErrorDetails error={error} />
      {isAIError && <AIRecoveryInfo />}
      <ErrorActions retryCount={retryCount} onRetry={onRetry} />

      <p style={{
        fontSize: '12px',
        color: '#888',
        marginTop: '20px',
        fontFamily: 'monospace'
      }}>
        Error ID: {Date.now().toString()} | Retry Count: {retryCount}
      </p>
    </div>
  </div>
);

const ErrorDetails = ({ error }) => (
  <div style={{
    backgroundColor: '#2a2a2a',
    padding: '15px',
    borderRadius: '8px',
    margin: '20px 0',
    textAlign: 'left'
  }}>
    <strong>Error Details:</strong>
    <p style={{ fontFamily: 'monospace', fontSize: '14px', color: '#ff6b6b' }}>
      {error?.message}
    </p>
  </div>
);

const AIRecoveryInfo = () => (
  <div style={{
    backgroundColor: '#1e3a5f',
    border: '1px solid #4a90e2',
    padding: '15px',
    borderRadius: '8px',
    margin: '20px 0'
  }}>
    <h3>🔧 AI Recovery Mode</h3>
    <p>The AI features encountered an issue. This could be due to:</p>
    <ul style={{ textAlign: 'left', paddingLeft: '20px' }}>
      <li>API rate limits or connectivity issues</li>
      <li>Temporary service unavailability</li>
      <li>Configuration problems</li>
    </ul>
    <p><strong>Recovery actions taken:</strong></p>
    <ul style={{ textAlign: 'left', paddingLeft: '20px' }}>
      <li>✅ Cleared AI cache</li>
      <li>✅ Reset error state</li>
      <li>✅ Prepared graceful fallbacks</li>
    </ul>
  </div>
);

const ErrorActions = ({ retryCount, onRetry }) => (
  <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
    {retryCount < 3 && (
      <button
        onClick={onRetry}
        style={{
          padding: '12px 24px',
          backgroundColor: '#4a90e2',
          color: 'white',
          border: 'none',
          borderRadius: '20px',
          cursor: 'pointer',
          fontWeight: 'bold'
        }}
      >
        🔄 Try Again {retryCount > 0 ? `(${retryCount}/3)` : ''}
      </button>
    )}

    <button
      onClick={() => window.location.reload()}
      style={{
        padding: '12px 24px',
        backgroundColor: '#1db954',
        color: 'white',
        border: 'none',
        borderRadius: '20px',
        cursor: 'pointer',
        fontWeight: 'bold'
      }}
    >
      🔃 Full Reload
    </button>
  </div>
);

// Enhanced AI-Aware Error Boundary Component
class ErrorBoundaryComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorId: null,
      isAIError: false,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
      errorId: Date.now().toString(),
      isAIError: detectAIError(error)
    };
  }

  componentDidCatch(error, errorInfo) {
    logger.error('App Error:', error, errorInfo);

    if (this.state.isAIError) {
      logAIError(error, errorInfo);
      this.handleAIErrorGracefully(error);
    }
  }

  handleAIErrorGracefully = (error) => {
    localStorage.setItem('lastAIError', JSON.stringify({
      message: error.message,
      timestamp: Date.now(),
      errorId: this.state.errorId
    }));

    clearAICache();
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorId: null,
      isAIError: false,
      retryCount: prevState.retryCount + 1
    }));
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorDisplay
          error={this.state.error}
          isAIError={this.state.isAIError}
          retryCount={this.state.retryCount}
          onRetry={this.handleRetry}
        />
      );
    }

    return this.props.children;
  }
}

// Platform management utilities
const pauseSpotifyPlayer = async () => {
  if (window._spotifyPlayerInstance) {
    try {
      await window._spotifyPlayerInstance.pause();
      logger.debug('Spotify paused via existing SDK controls');
    } catch (error) {
      logger.warn('Could not pause Spotify:', error);
    }
  }
};

const clearPlatformThemes = () => {
  document.body.classList.remove('platform-spotify', 'platform-youtube');
};

const applyPlatformTheme = (platform) => {
  clearPlatformThemes();
  document.body.classList.add(`platform-${platform}`);
  logger.debug(`Applied ${platform} theme on dashboard entry`);
};

// Removed resetSpotifyPlayer - now handled inline in handleDashboardEntry

const fetchCurrentPlatform = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/platform-status`, {
      credentials: 'include'
    });

    if (response.ok) {
      const data = await response.json();
      return data.currentPlatform;
    }
  } catch (error) {
    logger.error('Error fetching platform status:', error);
  }
  return null;
};

const handleDashboardExit = async () => {
  logger.debug('Left dashboard - auto-pausing and cleaning up...');
  await pauseSpotifyPlayer();
  clearPlatformThemes();
};

const handleDashboardEntry = async () => {
  logger.debug('Entered dashboard - fresh initialization like first load...');

  const currentPlatform = await fetchCurrentPlatform();

  if (currentPlatform) {
    applyPlatformTheme(currentPlatform);

    // ALWAYS do a complete fresh reset on dashboard entry
    // This makes every entry identical to the first working load
    if (currentPlatform === 'spotify') {
      logger.progress('Full Spotify player reset - treating like first load');

      // Disconnect and clear any existing player
      if (window._spotifyPlayerInstance) {
        try {
          await window._spotifyPlayerInstance.disconnect();
          logger.debug('Disconnected old player instance');
        } catch (error) {
          logger.warn('Error disconnecting player:', error);
        }
        delete window._spotifyPlayerInstance;
      }

      // Clear any cached tokens to force fresh fetch
      delete window._prefetchedTokens;

      logger.success('Clean slate ready - SpotifyPlayer will initialize fresh');
    }
  }
};

// Platform Theme Manager Hook - Enhanced with auto-controls
const usePlatformThemeManager = () => {
  const location = useLocation();

  useEffect(() => {
    const initializePlatformTheme = async () => {
      if (location.pathname !== '/dashboard') {
        await handleDashboardExit();
      } else {
        await handleDashboardEntry();
      }
    };

    initializePlatformTheme();
  }, [location.pathname]);

  useEffect(() => {
    return () => {
      clearPlatformThemes();
    };
  }, []);
};

// Enhanced AppRoutes with Platform Theme Manager
const AppRoutes = () => {
  const navigate = useNavigate();
  
  // Initialize platform theme manager
  usePlatformThemeManager();

  useEffect(() => {
    const section = new URLSearchParams(window.location.search).get("goto");
    if (section === "dashboard") {
      navigate("/dashboard");
    }
  }, [navigate]);

  return (
    <Routes>
      <Route path="/" element={<AuthHeroPage />} />
      {/* Legacy /auth/supabase route removed - AuthHeroPage now handles Supabase auth */}
      <Route path="/auth/callback" element={<OAuthCallback />} />
      <Route path="/platforms" element={<PlatformSelector />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/oauth-success" element={<OAuthSuccess />} />
      <Route path="/supabase-style-test" element={<SupabaseAuthStyleTest />} />
    </Routes>
  );
};

// Main App Component
function App() {
  return (
    <ErrorBoundaryComponent>
      <SupabaseAuthProvider>
        <SyncProvider>
          <Router>
            <AppRoutes />
          </Router>
        </SyncProvider>
      </SupabaseAuthProvider>
    </ErrorBoundaryComponent>
  );
}

export default App;